import { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { X, Loader2, CheckCircle2, AlertCircle, Table } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * Bulk Add Reports Modal — accepts pasted TSV/CSV data (like copying from Excel/Sheets)
 * Columns (in order):
 *   date | reader_number | classification | title | pages_read | is_completed | author (optional) | total_pages (optional)
 * User can paste dozens of rows at once. Preview validates before insert.
 */
const CLASS_OPTIONS = ['كتاب', 'مقالة', 'دراسة علمية', 'المساقات', 'التدبر'];
const HEADER_COLS = [
  { key: 'date', label: 'التاريخ (YYYY-MM-DD)' },
  { key: 'reader_number', label: 'الرقم القرائي' },
  { key: 'classification', label: 'التصنيف' },
  { key: 'title', label: 'العنوان' },
  { key: 'pages_read', label: 'الصفحات' },
  { key: 'is_completed', label: 'مكتمل (نعم/لا)' },
  { key: 'author', label: 'الكاتب (اختياري)' },
  { key: 'total_pages', label: 'إجمالي الصفحات (اختياري)' },
];

const parseRows = (raw) => {
  const lines = raw.split(/\r?\n/).map(l => l.trimEnd()).filter(l => l.trim().length > 0);
  return lines.map((line) => {
    // Support tab or comma separated
    const cells = line.includes('\t') ? line.split('\t') : line.split(',');
    const [date, reader_number, classification, title, pages_read, is_completed, author, total_pages] = cells.map(c => (c || '').trim());
    return {
      date: date || '',
      reader_number: reader_number || '',
      classification: classification || '',
      title: title || '',
      pages_read: pages_read || '',
      is_completed: is_completed || 'لا',
      author: author || '',
      total_pages: total_pages || '',
    };
  });
};

const validateRow = (row) => {
  const errors = [];
  if (!row.reader_number || !/^\d+$/.test(row.reader_number)) errors.push('الرقم');
  if (!row.title) errors.push('العنوان');
  if (!row.classification) errors.push('التصنيف');
  else if (!CLASS_OPTIONS.some(c => row.classification.includes(c))) errors.push('تصنيف غير معروف');
  if (row.pages_read && !/^\d+$/.test(row.pages_read)) errors.push('صفحات ≠ رقم');
  if (row.date && !/^\d{4}-\d{2}-\d{2}$/.test(row.date)) errors.push('تاريخ');
  return errors;
};

const BulkAddReportsModal = ({ onClose, onSuccess }) => {
  const [rawText, setRawText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const parsedRows = useMemo(() => parseRows(rawText), [rawText]);
  const rowValidations = useMemo(() => parsedRows.map(validateRow), [parsedRows]);
  const totalErrors = rowValidations.filter(e => e.length > 0).length;
  const validRows = parsedRows.length - totalErrors;

  const loadSample = () => {
    const sample = [
      '2026-01-15\t13431\tكتاب\tمقدمة ابن خلدون\t250\tنعم\tابن خلدون\t500',
      '2026-01-16\t13431\tمقالة\tأهمية القراءة\t8\tنعم\t\t',
      '2026-01-17\t82928\tالمساقات\tنور التوحيد\t33\tنعم\t\t',
    ].join('\n');
    setRawText(sample);
  };

  const submit = async () => {
    if (parsedRows.length === 0) { toast.error('لا توجد بيانات'); return; }
    if (totalErrors > 0) {
      if (!window.confirm(`يوجد ${totalErrors} صفوف بها أخطاء وستُتجاهل. المتابعة؟`)) return;
    }
    setSubmitting(true);
    try {
      // Only submit valid rows
      const rows = parsedRows.filter((_, i) => rowValidations[i].length === 0);
      const res = await api.post('/admin/reports/bulk-insert', { rows });
      setResult(res.data);
      toast.success(`تم إدراج ${res.data.inserted} تقرير — أُعيد حساب ${res.data.affected_readers} قارئ`);
      if (res.data.failed === 0) {
        setTimeout(() => onSuccess(), 800);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'فشل الإدراج');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="bulk-add-modal">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b bg-primary/5">
          <h2 className="text-xl font-bold text-accent flex items-center gap-2">
            <Table className="w-6 h-6" />
            إضافة تقارير قرائية دفعة واحدة
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded-lg" data-testid="close-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900 space-y-1">
            <p className="font-bold">طريقة الاستخدام:</p>
            <p>1. انسخ بيانات جدول من Excel أو Google Sheets (أعمدة مفصولة بـ Tab أو فاصلة)، ثم الصقها هنا.</p>
            <p>2. ترتيب الأعمدة المطلوب:</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {HEADER_COLS.map((c, i) => (
                <span key={c.key} className="bg-white border border-blue-300 rounded px-2 py-0.5 text-xs">
                  {i + 1}. {c.label}
                </span>
              ))}
            </div>
            <p className="text-xs mt-2">التصنيف يجب أن يحتوي إحدى الكلمات: {CLASS_OPTIONS.join(' · ')}</p>
          </div>

          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={loadSample} data-testid="load-sample-btn">تحميل مثال</Button>
            <Button size="sm" variant="ghost" onClick={() => setRawText('')}>مسح</Button>
            <span className="text-sm text-muted-foreground mr-auto">
              الصفوف: <b>{parsedRows.length}</b> · صالحة: <b className="text-emerald-600">{validRows}</b>
              {totalErrors > 0 && <> · أخطاء: <b className="text-red-600">{totalErrors}</b></>}
            </span>
          </div>

          <Textarea
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder="الصق البيانات هنا&#10;مثال:&#10;2026-01-15&#9;13431&#9;كتاب&#9;مقدمة ابن خلدون&#9;250&#9;نعم"
            className="min-h-[180px] font-mono text-xs"
            dir="ltr"
            data-testid="bulk-paste-area"
          />

          {parsedRows.length > 0 && (
            <div className="border rounded-lg overflow-x-auto max-h-[300px]">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 sticky top-0">
                  <tr>
                    <th className="p-2 text-right w-10">#</th>
                    {HEADER_COLS.map(c => (
                      <th key={c.key} className="p-2 text-right whitespace-nowrap">{c.label}</th>
                    ))}
                    <th className="p-2 text-right w-24">الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedRows.map((row, i) => {
                    const errs = rowValidations[i];
                    const bad = errs.length > 0;
                    return (
                      <tr key={i} className={bad ? 'bg-red-50' : 'hover:bg-muted/10'}>
                        <td className="p-2 text-muted-foreground">{i + 1}</td>
                        <td className="p-2">{row.date}</td>
                        <td className="p-2 font-mono">{row.reader_number}</td>
                        <td className="p-2">{row.classification}</td>
                        <td className="p-2 max-w-[200px] truncate">{row.title}</td>
                        <td className="p-2">{row.pages_read}</td>
                        <td className="p-2">{row.is_completed}</td>
                        <td className="p-2">{row.author}</td>
                        <td className="p-2">{row.total_pages}</td>
                        <td className="p-2">
                          {bad ? (
                            <span className="inline-flex items-center gap-1 text-red-600" title={errs.join(', ')}>
                              <AlertCircle className="w-3.5 h-3.5" /> {errs.join('·')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-emerald-600">
                              <CheckCircle2 className="w-3.5 h-3.5" /> صالح
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {result && (
            <div className={`rounded-lg p-3 text-sm ${result.failed === 0 ? 'bg-emerald-50 text-emerald-900' : 'bg-amber-50 text-amber-900'}`}>
              <p className="font-bold">النتيجة:</p>
              <p>أُدرج {result.inserted} تقرير · فشل {result.failed} · أُعيد حساب {result.affected_readers} قارئ</p>
              {result.errors?.length > 0 && (
                <ul className="mt-2 list-disc pr-5 max-h-32 overflow-y-auto">
                  {result.errors.slice(0, 20).map((e, i) => (
                    <li key={i}>الصف {e.row}: {e.error}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t flex justify-end gap-3 bg-muted/20">
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button
            onClick={submit}
            disabled={submitting || validRows === 0}
            className="bg-primary text-white hover:bg-primary/90"
            data-testid="bulk-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
            إدراج {validRows} تقرير
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkAddReportsModal;
